import React, { useState, useMemo } from 'react';
import type { InstrumentType } from '../../types/composition';
import { instrumentDatabase } from '../../services/instruments/InstrumentDatabase';
import type { InstrumentCategory } from '../../types/instruments';
import './InstrumentSelectorPanel.css';

/**
 * Props for InstrumentSelectorPanel component
 */
export interface InstrumentSelectorPanelProps {
  /** Currently selected instrument */
  currentInstrument: InstrumentType;
  /** Callback when instrument is selected */
  onInstrumentSelect: (instrument: InstrumentType) => void;
  /** Callback when panel is closed */
  onClose: () => void;
}

/**
 * InstrumentSelectorPanel Component
 * 
 * Mobile-friendly full-screen panel for selecting instruments.
 * Displays instruments grouped by category with icons and descriptions.
 * Users can scroll through categories and tap to select an instrument.
 */
export const InstrumentSelectorPanel: React.FC<InstrumentSelectorPanelProps> = ({
  currentInstrument,
  onInstrumentSelect,
  onClose,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<InstrumentCategory | 'All'>('All');

  // Get all instruments from database
  const allInstruments = useMemo(() => {
    return instrumentDatabase.getAllInstruments();
  }, []);

  // Get unique categories
  const categories: Array<InstrumentCategory | 'All'> = useMemo(() => {
    const cats = new Set<InstrumentCategory>();
    allInstruments.forEach(inst => cats.add(inst.category));
    return ['All', ...Array.from(cats).sort()];
  }, [allInstruments]);

  // Filter instruments by selected category
  const filteredInstruments = useMemo(() => {
    if (selectedCategory === 'All') {
      return allInstruments;
    }
    return allInstruments.filter(inst => inst.category === selectedCategory);
  }, [allInstruments, selectedCategory]);

  /**
   * Handle instrument selection
   */
  const handleInstrumentSelect = (instrument: InstrumentType) => {
    onInstrumentSelect(instrument);
    onClose();
  };

  return (
    <>
      {/* Backdrop overlay */}
      <div 
        className="instrument-selector-panel-backdrop" 
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Panel container */}
      <div 
        className="instrument-selector-panel" 
        role="dialog" 
        aria-label="Instrument selector"
        aria-modal="true"
      >
        {/* Header */}
        <div className="instrument-selector-panel-header">
          <h2 className="instrument-selector-panel-title">Select Instrument</h2>
          <button
            className="instrument-selector-panel-close"
            onClick={onClose}
            aria-label="Close instrument selector"
            title="Close"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true"></i>
          </button>
        </div>

        {/* Category Filter */}
        <div className="instrument-selector-categories">
          {categories.map((category) => (
            <button
              key={category}
              className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category)}
              aria-label={`Filter by ${category}`}
              aria-pressed={selectedCategory === category}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Instrument List */}
        <div className="instrument-selector-content">
          <div className="instrument-list" role="list">
            {filteredInstruments.map((instrument) => (
              <button
                key={instrument.id}
                className={`instrument-card ${currentInstrument === instrument.id ? 'selected' : ''}`}
                onClick={() => handleInstrumentSelect(instrument.id)}
                role="listitem"
                aria-label={`Select ${instrument.displayName}`}
                aria-current={currentInstrument === instrument.id ? 'true' : undefined}
              >
                <div className="instrument-card-icon">
                  {instrument.icon}
                </div>
                <div className="instrument-card-content">
                  <div className="instrument-card-name">
                    {instrument.displayName}
                  </div>
                  <div className="instrument-card-category">
                    {instrument.category}
                  </div>
                  {instrument.description && (
                    <div className="instrument-card-description">
                      {instrument.description}
                    </div>
                  )}
                </div>
                {currentInstrument === instrument.id && (
                  <div className="instrument-card-check">
                    <i className="fa-solid fa-check" aria-hidden="true"></i>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};
